import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Button,
  Paper,
  Card,
  CardContent,
  CardActions,
  Divider,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  LinearProgress,
  useTheme,
} from '@mui/material';
import { 
  People as PeopleIcon,
  Person as PersonIcon 
} from '@mui/icons-material';
import { format } from 'date-fns';
import { getPublicJobDetailsWithRegistrations, registerForJob } from '../services/api';
import { JobDetailsWithRegistrations, Timeslot, Registration, Volunteer } from '../models/types';
import { useAuth } from '../contexts/AuthContext';
import VolunteerRegistrationForm from '../components/VolunteerRegistrationForm';
import { de } from 'date-fns/locale';


const JobDetailPage: React.FC = () => {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate = useNavigate();  const { volunteer } = useAuth();
  const theme = useTheme();
  
  const [jobDetails, setJobDetails] = useState<JobDetailsWithRegistrations | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [registering, setRegistering] = useState<string | null>(null);
  const [registerError, setRegisterError] = useState<string | null>(null);
  const [registerSuccess, setRegisterSuccess] = useState<string | null>(null);  const [volunteerRegistrations, setVolunteerRegistrations] = useState<Registration[]>([]);
  const [registrationDialogOpen, setRegistrationDialogOpen] = useState(false);
  const [pendingTimeslotId, setPendingTimeslotId] = useState<string | null>(null);  const [confirmationDialogOpen, setConfirmationDialogOpen] = useState(false);
  const [selectedTimeslotForConfirmation, setSelectedTimeslotForConfirmation] = useState<string | null>(null);
  const [registrationComment, setRegistrationComment] = useState<string>('');
  const [newlyRegisteredVolunteer, setNewlyRegisteredVolunteer] = useState<Volunteer | null>(null);
  const [confirmationLoading, setConfirmationLoading] = useState(false);useEffect(() => {
    const fetchJobDetails = async () => {
      if (!jobId) return;
      
      try {
        setLoading(true);
        // If we have a volunteer, use the consolidated API endpoint
        if (volunteer) {
          const data = await getPublicJobDetailsWithRegistrations(jobId, volunteer.id);
          setJobDetails(data);
          setVolunteerRegistrations(data.volunteerRegistrations || []);
        } else {
          // If no volunteer, just get the job details
          const data = await getPublicJobDetailsWithRegistrations(jobId);
          setJobDetails(data);
        }
        setError(null);
      } catch (err) {
        console.error('Error fetching job details:', err);
        setError('Failed to load job details. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchJobDetails();
  }, [jobId, volunteer]);

  const checkTimeConflict = (timeslot: Timeslot) => {
    return volunteerRegistrations.some(reg => {
      if (!reg.timeslotStart || !reg.timeslotEnd) return false;

      const timeslotStart = new Date(timeslot.startTime).getTime();
      const timeslotEnd = new Date(timeslot.endTime).getTime();
      const existingStart = new Date(reg.timeslotStart).getTime();
      const existingEnd = new Date(reg.timeslotEnd).getTime();
      
      return (
        (timeslotStart >= existingStart && timeslotStart < existingEnd) ||
        (timeslotEnd > existingStart && timeslotEnd <= existingEnd) ||
        (timeslotStart <= existingStart && timeslotEnd >= existingEnd)
      );
    });
  };
  
  const isTimeslotFull = (timeslot: Timeslot) => {
    return timeslot.availableSpots === 0 || 
          (timeslot.requiredVolunteers - (timeslot.filledSpots || 0)) <= 0;
  };

  const renderCoverageIndicator = (timeslot: Timeslot) => {
    if (!timeslot.filledSpots) return null;
    
    const filledSpots = timeslot.filledSpots || 0;
    const totalSpots = timeslot.requiredVolunteers;
    const coveragePercent = (filledSpots / totalSpots) * 100;
    
    let color = 'success';
    let label = 'Ausreichend besetzt';
    
    if (coveragePercent < 50) {
      color = 'error';
      label = 'Viele freie Plätze';
    } else if (coveragePercent < 75) {
      color = 'warning';
      label = 'Noch Plätze frei';
    }
    
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column', mt: 1.5, width: '100%' }}>
        <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', mb: 0.5 }}>
          <LinearProgress 
            variant="determinate" 
            value={Math.min(100, coveragePercent)} 
            color={color as any}
            sx={{ 
              height: 8, 
              borderRadius: 4, 
              width: '100%',
              backgroundColor: theme.palette.grey[200] 
            }}
          />
          <Typography variant="caption" sx={{ ml: 1, whiteSpace: 'nowrap' }}>
            {`${filledSpots}/${totalSpots}`}
          </Typography>
        </Box>        <Typography variant="caption" color={color as any} sx={{ fontWeight: 500 }}>
          {label} {(timeslot.availableSpots || 0) > 0 && `(${timeslot.availableSpots || 0} Plätze frei)`}
        </Typography>
      </Box>
    );
  };

  const renderWhoIsHelping = (timeslot: Timeslot) => {
    const filledSpots = timeslot.filledSpots || 0;
    
    // Get actual registrations for this timeslot from ALL registrations (not just current user)
    const timeslotRegistrations = jobDetails?.timeslotRegistrations?.filter(
      reg => reg.timeslotId === timeslot.id
    ) || [];
    
    // Use only real volunteer names - no placeholders
    const actualVolunteers = timeslotRegistrations
      .filter(reg => reg.volunteerName)
      .map(reg => reg.volunteerName!);
    
    // Only show the section if there are filled spots
    if (filledSpots === 0) {
      return (
        <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 2, border: '1px dashed', borderColor: 'grey.300' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <PeopleIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
            <Typography variant="subtitle2" color="text.secondary" sx={{ fontWeight: 600 }}>
              Wer hilft noch?
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', textAlign: 'center' }}>
            Noch keine Anmeldungen - sei die/der Erste! 🙋‍♀️
          </Typography>
        </Box>
      );
    }

    // Only show if we have actual volunteer names
    if (actualVolunteers.length === 0) {
      return (
        <Box sx={{ 
          mt: 2, 
          p: 2, 
          bgcolor: 'info.50', 
          borderRadius: 2, 
          border: '1px solid', 
          borderColor: 'info.200'
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
            <PeopleIcon fontSize="small" sx={{ mr: 1, color: 'info.main' }} />
            <Typography variant="subtitle2" color="info.main" sx={{ fontWeight: 700, fontSize: '0.9rem' }}>
              Helfer angemeldet ({filledSpots} {filledSpots === 1 ? 'Person' : 'Personen'})
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
            Namen werden geladen...
          </Typography>
        </Box>
      );
    }

    return (
      <Box sx={{ 
        mt: 2, 
        p: 2, 
        bgcolor: 'success.50', 
        borderRadius: 2, 
        border: '1px solid', 
        borderColor: 'success.200',
        background: 'linear-gradient(135deg, rgba(46, 125, 50, 0.05) 0%, rgba(46, 125, 50, 0.1) 100%)'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <PeopleIcon fontSize="small" sx={{ mr: 1, color: 'success.main' }} />
          <Typography variant="subtitle2" color="success.main" sx={{ fontWeight: 700, fontSize: '0.9rem' }}>
            Wer hilft noch? 
          </Typography>
        </Box>
        
        {/* Name Chips - Only Real Volunteers */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.8 }}>
          {actualVolunteers.map((name, index) => (
            <Chip
              key={index}
              label={name}
              size="small"
              variant="filled"
              color="success"
              sx={{ 
                height: 28,
                fontSize: '0.8rem',
                fontWeight: 600,
                '&:hover': {
                  backgroundColor: 'success.dark',
                  transform: 'scale(1.05)',
                },
                transition: 'all 0.2s ease'
              }}
              icon={<PersonIcon fontSize="small" />}
            />
          ))}
          
          {filledSpots > actualVolunteers.length && (
            <Chip
              label={`+${filledSpots - actualVolunteers.length} weitere angemeldet`}
              size="small"
              variant="outlined"
              color="success"
              sx={{ 
                height: 28,
                fontSize: '0.8rem',
                fontWeight: 500,
                fontStyle: 'italic',
                opacity: 0.8
              }}
            />
          )}
        </Box>
      </Box>
    );
  };
  const handleRegister = async (timeslotId: string, volunteerRegistered: Volunteer, comment?: string) => {
    if (!volunteer && !volunteerRegistered) {
      console.log('No volunteer data, redirecting to registration');
      handleRegisterAndSignup(timeslotId);
      return;
    }
    const volunteerData = volunteer || volunteerRegistered;
    
    if (!jobId) {
      console.error('No jobId available');
      return;
    }
    
    if (!volunteerData.id) {
      console.error('Volunteer data incomplete', volunteer);
      setRegisterError('Error with volunteer data. Please try logging in again.');
      return;
    }
    
    try {
      setRegistering(timeslotId);
      setRegisterError(null);
      
      console.log('Registering volunteer', {volunteerId: volunteerData.id, jobId, timeslotId, comment});      await registerForJob({
        volunteerId: volunteerData.id,
        jobId: jobId,
        timeslotId: timeslotId,
        comment: comment?.trim() || undefined
      });
      
      setRegisterSuccess('Anmeldung erfolgreich! Vielen Dank für Ihre Hilfe.');
        
      // Refresh job details and registrations with one call
      const updatedData = await getPublicJobDetailsWithRegistrations(jobId, volunteerData.id);
      setJobDetails(updatedData);
      setVolunteerRegistrations(updatedData.volunteerRegistrations || []);
      
    } catch (err: any) {
      console.error('Error registering for job:', err);
      setRegisterError(err.response?.data || 'Registrierung fehlgeschlagen. Bitte versuchen Sie es erneut.');
    } finally {
      setRegistering(null);
    }
  };

  const handleShowConfirmationDialog = (timeslotId: string) => {
    setSelectedTimeslotForConfirmation(timeslotId);
    setRegistrationComment('');
    setConfirmationDialogOpen(true);
  };  const handleConfirmRegistration = async () => {
    if (selectedTimeslotForConfirmation) {
      try {
        setConfirmationLoading(true);
        
        // Use newly registered volunteer if available, otherwise use current volunteer
        const volunteerToUse = newlyRegisteredVolunteer || volunteer;
        if (volunteerToUse) {
          await handleRegister(selectedTimeslotForConfirmation, volunteerToUse, registrationComment);
        }
        
        // Close dialog and reset state only after successful registration
        setConfirmationDialogOpen(false);
        setSelectedTimeslotForConfirmation(null);
        setRegistrationComment('');
        setNewlyRegisteredVolunteer(null);
        setPendingTimeslotId(null);
      } catch (error) {
        // Error handling is already done in handleRegister
        console.error('Error in confirmation registration:', error);
      } finally {
        setConfirmationLoading(false);
      }
    }
  };  const handleCancelRegistration = () => {
    if (confirmationLoading) return; // Prevent cancellation during loading
    
    setConfirmationDialogOpen(false);
    setSelectedTimeslotForConfirmation(null);
    setRegistrationComment('');
    setNewlyRegisteredVolunteer(null);
    setPendingTimeslotId(null);
  };

  const handleRegisterAndSignup = (timeslotId: string) => {
    setPendingTimeslotId(timeslotId);
    setRegistrationDialogOpen(true);
  };  const handleRegistrationSuccess = async (newVolunteer: Volunteer) => {
    setRegistrationDialogOpen(false);
    if (pendingTimeslotId) {
      // Store the new volunteer and show confirmation dialog
      setNewlyRegisteredVolunteer(newVolunteer);
      setSelectedTimeslotForConfirmation(pendingTimeslotId);
      setRegistrationComment('');
      setConfirmationDialogOpen(true);
      // Note: We don't reset pendingTimeslotId here as we need it for the confirmation
    }
  };

  const handleRegistrationCancel = () => {
    setRegistrationDialogOpen(false);
    setPendingTimeslotId(null);
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />          <Typography variant="body1" sx={{ mt: 2 }}>
          Lade Job-Details...
        </Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">{error}</Alert>
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Button variant="contained" onClick={() => navigate('/jobs')}>
            Back to Jobs
          </Button>
        </Box>
      </Container>
    );
  }

  if (!jobDetails) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>        <Alert severity="error">Job nicht gefunden</Alert>
        <Box sx={{ mt: 2, textAlign: 'center' }}>
          <Button variant="contained" onClick={() => navigate('/jobs')}>
            Zurück zur Jobübersicht
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box sx={{ mt: 4, mb: 4 }}>        <Button variant="outlined" onClick={() => navigate('/jobs')} sx={{ mb: 2 }}>
          Zurück zur Jobübersicht
        </Button>

        {!volunteer && (
          <Paper elevation={2} sx={{ p: 2, mb: 2, bgcolor: 'primary.light' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6" color="white">                  Als Helfer registrieren
                </Typography>
                <Typography variant="body1" color="white">
                  Sie müssen sich zuerst registrieren, bevor Sie sich für Jobs anmelden können
                </Typography>
              </Box>              <Button 
                variant="contained"
                color="secondary"
                size="large"
                onClick={() => setRegistrationDialogOpen(true)}
              >
                Jetzt registrieren
              </Button>
            </Box>
          </Paper>
        )}

        {registerSuccess && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {registerSuccess}
          </Alert>
        )}

        {registerError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {registerError}
          </Alert>
        )}        <Paper elevation={2} sx={{ p: 3, mb: 4 }}>
          <Typography variant="h4" gutterBottom>
            {jobDetails.job.name}
          </Typography>
          
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>                        <Chip 
              label={`Ort: ${jobDetails.job.location}`} 
              variant="outlined"
            />
            {jobDetails.job.responsible && (
              <Chip 
                label={`Verantwortlich: ${jobDetails.job.responsible}`} 
                variant="outlined"
              />
            )}
          </Box>
          
          <Typography variant="body1" paragraph>
            {jobDetails.job.description}
          </Typography>
          
          {/* Job coverage overview */}
          {jobDetails.timeslots.length > 0 && (
            <>
              <Divider sx={{ my: 2 }} />
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Aktuelle Besetzung
                </Typography>
                
                {(() => {                  // Calculate overall job coverage
                  const totalRequired = jobDetails.timeslots.reduce((sum: number, slot: Timeslot) => sum + slot.requiredVolunteers, 0);
                  const totalFilled = jobDetails.timeslots.reduce((sum: number, slot: Timeslot) => sum + (slot.filledSpots || 0), 0);
                  const overallPercent = totalRequired > 0 ? (totalFilled / totalRequired) * 100 : 0;
                  
                  let statusColor = 'success';
                  let statusLabel = 'Gut besetzt';
                  
                  if (overallPercent < 50) {
                    statusColor = 'error';
                    statusLabel = 'Dringend Helfer benötigt';
                  } else if (overallPercent < 75) {
                    statusColor = 'warning';
                    statusLabel = 'Weitere Helfer benötigt';
                  }
                  
                  return (
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 0.5 }}>
                        <LinearProgress 
                          variant="determinate" 
                          value={Math.min(100, overallPercent)} 
                          color={statusColor as any}
                          sx={{ 
                            height: 10, 
                            borderRadius: 5, 
                            width: '100%',
                            backgroundColor: theme.palette.grey[200] 
                          }}
                        />
                        <Typography variant="body2" sx={{ ml: 1, whiteSpace: 'nowrap' }}>
                          {`${totalFilled}/${totalRequired}`}
                        </Typography>
                      </Box>
                      
                      <Typography variant="body2" color={statusColor as any} sx={{ fontWeight: 500 }}>
                        {statusLabel} {overallPercent < 100 && `(${totalRequired - totalFilled} weitere Helfer werden benötigt)`}
                      </Typography>
                    </Box>
                  );
                })()}
              </Box>
            </>
          )}
        </Paper><Typography variant="h5" gutterBottom>
          Verfügbare Zeitfenster
        </Typography>        {jobDetails.timeslots.length === 0 ? (
          <Alert severity="info">
            Für diesen Job sind noch keine Zeitfenster verfügbar. Bitte schauen Sie später noch einmal vorbei.
          </Alert>
        ) : (
          <>
            {Object.entries(
              [...jobDetails.timeslots]
                // First sort all timeslots
                .sort((a, b) => {
                  const dateCompare = new Date(a.startTime).getTime() - new Date(b.startTime).getTime();
                  if (dateCompare === 0) {
                    const aNeeded = a.requiredVolunteers - (a.filledSpots || 0);
                    const bNeeded = b.requiredVolunteers - (b.filledSpots || 0);
                    return bNeeded - aNeeded;
                  }
                  return dateCompare;
                })
                // Then group by date
                .reduce((groups, timeslot) => {
                  const date = format(new Date(timeslot.startTime), 'yyyy-MM-dd');
                  if (!groups[date]) {
                    groups[date] = [];
                  }
                  groups[date].push(timeslot);
                  return groups;
                }, {} as { [key: string]: Timeslot[] })
            ).map(([date, timeslots]) => (
              <Box key={date} sx={{ width: '100%', mb: 4 }}>
                <Typography variant="h6" sx={{ mb: 2, borderBottom: 1, borderColor: 'divider', pb: 1 }}>
                  {format(new Date(date), 'EEEE, d. MMMM yyyy', { locale: de })}
                </Typography>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>                  {timeslots.map((timeslot: Timeslot) => (
                    <Box key={timeslot.id} sx={{ flexBasis: { xs: '100%', sm: 'calc(50% - 24px)', md: 'calc(33.333% - 24px)' }, flexGrow: 0 }}>
                      <Card sx={{ 
                  borderLeft: 4,
                  borderLeftColor: 
                    (timeslot.filledSpots || 0) / timeslot.requiredVolunteers < 0.5 ? 'error.main' :
                    (timeslot.filledSpots || 0) / timeslot.requiredVolunteers < 0.75 ? 'warning.main' : 
                    'success.main'
                }}>
                  <CardContent>                    <Typography variant="h6" gutterBottom>
                      {timeslot.description || 'Helferschicht'}
                    </Typography>                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      <strong>Ort:</strong> {jobDetails.job.location}
                    </Typography>
                    
                    {jobDetails.job.responsible && (
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        <strong>Verantwortlich:</strong> {jobDetails.job.responsible}
                      </Typography>
                    )}
                    
                    <Typography variant="body2" color="text.secondary">
                      <strong>Beginn:</strong> {format(new Date(timeslot.startTime), 'HH:mm')}
                    </Typography>
                    
                    <Typography variant="body2" color="text.secondary">
                      <strong>Ende:</strong> {format(new Date(timeslot.endTime), 'HH:mm')}
                    </Typography>

                    <Divider sx={{ my: 1.5 }} />
                    
                    <Typography variant="body2">
                      <strong>Benötigte Helfer:</strong> {timeslot.requiredVolunteers}
                    </Typography>
                    
                    <Typography variant="body2">
                      <strong>Freie Plätze:</strong> {
                        timeslot.availableSpots !== undefined 
                          ? timeslot.availableSpots
                          : timeslot.requiredVolunteers - (timeslot.filledSpots || 0)
                      }
                    </Typography>

                    {volunteer && checkTimeConflict(timeslot) && (                      <Alert severity="warning" sx={{ mt: 1.5 }}>
                        Dieses Zeitfenster überschneidet sich mit Ihren bestehenden Anmeldungen
                      </Alert>
                    )}

                    {renderCoverageIndicator(timeslot)}
                    
                    {/* Show who is helping section */}
                    {renderWhoIsHelping(timeslot)}
                  </CardContent>
                  
                  <CardActions>
                    {isTimeslotFull(timeslot) ? (
                      <Button 
                        fullWidth 
                        variant="outlined" 
                        color="error" 
                        disabled
                      >
                        Ausgebucht
                      </Button>
                    ) : volunteer && checkTimeConflict(timeslot) ? (
                      <Button 
                        fullWidth 
                        variant="outlined" 
                        color="warning" 
                        disabled
                      >                        Zeitkonflikt
                      </Button>                    ) : volunteer ? (
                      <Button
                        fullWidth
                        variant="contained"
                        color="primary"
                        onClick={() => handleShowConfirmationDialog(timeslot.id)}
                        disabled={registering === timeslot.id}
                      >
                        {registering === timeslot.id ? 'Registriere...' : 'hier Helfen'}
                      </Button>
                    ) : (
                      <Button
                        fullWidth
                        variant="contained"
                        color="primary"                      onClick={() => handleRegisterAndSignup(timeslot.id)}
                      >
                        Registrieren & hier Helfen
                      </Button>
                    )}
                  </CardActions>
                </Card>
                    </Box>
                  ))}
                </Box>
              </Box>
            ))}
          </>        )}

      {/* Confirmation Dialog for Registration */}      <Dialog 
        open={confirmationDialogOpen} 
        onClose={handleCancelRegistration}
        maxWidth="sm"
        fullWidth        disableEscapeKeyDown={confirmationLoading}
      >
        {/* Loading progress bar at the top of dialog */}
        {confirmationLoading && (
          <Box sx={{ width: '100%' }}>
            <LinearProgress />
          </Box>
        )}
        
        <DialogTitle>
          {newlyRegisteredVolunteer ? 'Registrierung abschließen' : 'Anmeldung bestätigen'}
        </DialogTitle>
        <DialogContent>
          {selectedTimeslotForConfirmation && jobDetails && (
            <>
              {(() => {
                const timeslot = jobDetails.timeslots.find(ts => ts.id === selectedTimeslotForConfirmation);
                if (!timeslot) return null;
                
                return (
                  <>
                    <Typography variant="body1" gutterBottom>
                      {newlyRegisteredVolunteer 
                        ? `Hallo ${newlyRegisteredVolunteer.name}! Möchten Sie sich für das folgende Zeitfenster anmelden?`
                        : 'Möchten Sie sich für das folgende Zeitfenster anmelden?'
                      }
                    </Typography>
                    <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                        {jobDetails.job.name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {timeslot.description || 'Helferschicht'}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Datum:</strong> {format(new Date(timeslot.startTime), 'EEEE, dd.MM.yyyy', { locale: de })}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Zeit:</strong> {format(new Date(timeslot.startTime), 'HH:mm')} - {format(new Date(timeslot.endTime), 'HH:mm')} Uhr
                      </Typography>
                      <Typography variant="body2">
                        <strong>Ort:</strong> {jobDetails.job.location}
                      </Typography>
                      {jobDetails.job.responsible && (
                        <Typography variant="body2">
                          <strong>Verantwortlich:</strong> {jobDetails.job.responsible}
                        </Typography>
                      )}
                    </Box>
                      <TextField
                      label="Kommentar (optional)"
                      multiline
                      rows={3}
                      value={registrationComment}
                      onChange={(e) => setRegistrationComment(e.target.value)}
                      placeholder="Zusätzliche Informationen zu Ihrer Anmeldung..."
                      fullWidth
                      disabled={confirmationLoading}
                      sx={{ mt: 3 }}
                    />
                  </>
                );
              })()}
            </>
          )}
        </DialogContent>        <DialogActions>
          <Button 
            onClick={handleCancelRegistration}
            disabled={confirmationLoading}
          >
            Abbrechen
          </Button>
          <Button 
            onClick={handleConfirmRegistration}
            variant="contained"
            color="primary"
            disabled={confirmationLoading}
            startIcon={confirmationLoading ? <CircularProgress size={20} /> : undefined}
          >
            {confirmationLoading 
              ? 'Wird verarbeitet...' 
              : (newlyRegisteredVolunteer ? 'Anmeldung abschließen' : 'Anmeldung bestätigen')
            }
          </Button>
        </DialogActions>
      </Dialog>

      {/* Registration Dialog */}
      <Dialog open={registrationDialogOpen} onClose={handleRegistrationCancel}>
        <VolunteerRegistrationForm 
          onSuccess={handleRegistrationSuccess}
        />
      </Dialog></Box>
    </Container>
  );
};

export default JobDetailPage;

